// Auth: web Google Sign-In (Google Identity Services) + dev-token injection for
// automated testing (mirrors the iOS -devSession pattern). The session token is
// the same HS256 JWT the iOS app uses; the API verifies it identically.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, authGoogle, authApple, fetchMe, setReauthHandler, setToken, getToken } from "./apiClient";
import type { AuthUser } from "./types";

/** Decode the session JWT payload (no verification) for immediate display. */
function decodeToken(token: string): { user: AuthUser; expired: boolean } | null {
  try {
    const p = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    ) as { sub?: string; email?: string; name?: string; exp?: number };
    if (!p.sub) return null;
    return {
      user: { userId: p.sub, email: p.email ?? null, name: p.name ?? null, createdAt: null },
      expired: typeof p.exp === "number" && p.exp * 1000 <= Date.now(),
    };
  } catch {
    return null;
  }
}

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string) || "";
// Apple "Services ID" (NOT the iOS bundle id) for Sign in with Apple on the web.
// Empty disables the Apple button. Apple requires an HTTPS redirectURI registered
// in the developer portal — it will NOT work on http://localhost, only on the
// production domain (https://projects.cedarpost.com).
const APPLE_SERVICES_ID = (import.meta.env.VITE_APPLE_SERVICES_ID as string) || "";

interface GsiCredentialResponse {
  credential: string;
}
interface GsiId {
  initialize(cfg: {
    client_id: string;
    callback: (r: GsiCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }): void;
  renderButton(el: HTMLElement, opts: Record<string, unknown>): void;
  prompt(): void;
}
interface AppleAuth {
  init(cfg: { clientId: string; scope: string; redirectURI: string; usePopup: boolean }): void;
  signIn(): Promise<{
    authorization: { id_token: string; code: string };
    user?: { name?: { firstName?: string; lastName?: string }; email?: string };
  }>;
}
declare global {
  interface Window {
    google?: { accounts?: { id?: GsiId } };
    AppleID?: { auth: AppleAuth };
  }
}

interface AuthState {
  user: AuthUser | null;
  status: "loading" | "signedOut" | "signedIn";
  googleConfigured: boolean;
  appleConfigured: boolean;
  signInWithGoogle: () => void;
  signInWithApple: () => void;
  signOut: () => void;
  /** Imperatively render the official Google button into a container. */
  renderGoogleButton: (el: HTMLElement) => void;
}

const AuthCtx = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

function consumeDevTokenFromUrl(): string | null {
  try {
    const url = new URL(window.location.href);
    const t = url.searchParams.get("devToken") || url.searchParams.get("token");
    if (t) {
      setToken(t);
      url.searchParams.delete("devToken");
      url.searchParams.delete("token");
      window.history.replaceState({}, "", url.toString());
      return t;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");
  const gsiReady = useRef(false);
  const pendingResolve = useRef<((t: string | null) => void) | null>(null);

  const handleCredential = useCallback(async (idToken: string): Promise<string | null> => {
    try {
      const { sessionToken, user: u } = await authGoogle(idToken);
      setToken(sessionToken);
      setUser(u);
      setStatus("signedIn");
      return sessionToken;
    } catch {
      setStatus("signedOut");
      return null;
    }
  }, []);

  const ensureGsi = useCallback((): GsiId | null => {
    const id = window.google?.accounts?.id;
    if (!id || !GOOGLE_CLIENT_ID) return null;
    if (!gsiReady.current) {
      id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (r) => {
          const resolve = pendingResolve.current;
          pendingResolve.current = null;
          handleCredential(r.credential).then((t) => resolve?.(t));
        },
        cancel_on_tap_outside: true,
      });
      gsiReady.current = true;
    }
    return id;
  }, [handleCredential]);

  // Bootstrap: dev-token from URL, or an existing session, then load the user.
  // Resilient to flaky networks: a valid (unexpired) token signs you in
  // immediately from its claims, and we only sign OUT on a definitive 401 —
  // never on a transient fetch error (which would otherwise log you out).
  useEffect(() => {
    let cancelled = false;
    consumeDevTokenFromUrl();
    const token = getToken();
    if (!token) {
      setStatus("signedOut");
      return;
    }
    const decoded = decodeToken(token);
    if (!decoded || decoded.expired) {
      setToken(null);
      setStatus("signedOut");
      return;
    }
    // Optimistic: trust the token; show the user now.
    setUser(decoded.user);
    setStatus("signedIn");
    // Refresh the canonical user in the background; tolerate transient failures.
    fetchMe()
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) {
          setToken(null);
          setUser(null);
          setStatus("signedOut");
        }
        // transient/network error → keep the optimistic signed-in session
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Register silent re-auth on 401: re-trigger the Google prompt.
  useEffect(() => {
    setReauthHandler(async () => {
      const id = ensureGsi();
      if (!id) return null;
      return new Promise<string | null>((resolve) => {
        pendingResolve.current = resolve;
        id.prompt();
      });
    });
    return () => setReauthHandler(null);
  }, [ensureGsi]);

  const signInWithGoogle = useCallback(() => {
    const id = ensureGsi();
    if (!id) return;
    id.prompt();
  }, [ensureGsi]);

  // Sign in with Apple (web). Lazy-loads the AppleID JS SDK, inits with the
  // Services ID, opens the popup, and exchanges the identity token for a session.
  const appleReady = useRef(false);
  const ensureApple = useCallback(async (): Promise<boolean> => {
    if (!APPLE_SERVICES_ID) return false;
    if (!window.AppleID) {
      await new Promise<void>((resolve, reject) => {
        const existing = document.getElementById("apple-jsapi");
        if (existing) {
          existing.addEventListener("load", () => resolve());
          existing.addEventListener("error", () => reject(new Error("apple sdk")));
          return;
        }
        const s = document.createElement("script");
        s.id = "apple-jsapi";
        s.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("apple sdk"));
        document.head.appendChild(s);
      }).catch(() => undefined);
    }
    if (!window.AppleID) return false;
    if (!appleReady.current) {
      window.AppleID.auth.init({
        clientId: APPLE_SERVICES_ID,
        scope: "name email",
        redirectURI: window.location.origin,
        usePopup: true,
      });
      appleReady.current = true;
    }
    return true;
  }, []);

  const signInWithApple = useCallback(async () => {
    try {
      if (!(await ensureApple()) || !window.AppleID) return;
      const data = await window.AppleID.auth.signIn();
      const idToken = data.authorization?.id_token;
      if (!idToken) return;
      const n = data.user?.name;
      const name = n ? [n.firstName, n.lastName].filter(Boolean).join(" ") || undefined : undefined;
      const { sessionToken, user: u } = await authApple(idToken, name);
      setToken(sessionToken);
      setUser(u);
      setStatus("signedIn");
    } catch {
      /* user cancelled, popup blocked, or SDK unavailable — stay signed out */
    }
  }, [ensureApple]);

  const renderGoogleButton = useCallback(
    (el: HTMLElement) => {
      const id = ensureGsi();
      if (!id) return;
      id.renderButton(el, { theme: "filled_black", size: "large", shape: "pill", text: "continue_with", width: 280 });
    },
    [ensureGsi],
  );

  const signOut = useCallback(() => {
    setToken(null);
    setUser(null);
    setStatus("signedOut");
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      status,
      googleConfigured: !!GOOGLE_CLIENT_ID,
      appleConfigured: !!APPLE_SERVICES_ID,
      signInWithGoogle,
      signInWithApple,
      signOut,
      renderGoogleButton,
    }),
    [user, status, signInWithGoogle, signInWithApple, signOut, renderGoogleButton],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
